<template>
  <component
    :is="embedded ? 'div' : 'v-container'"
    :fluid="!embedded"
    :class="embedded ? 'intelligence-discover' : 'intelligence-discover pa-4'"
  >
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Smart Search
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Search and discover talent across your organization using
          evidence-based matching
        </p>
        <v-alert
          v-if="fromSage"
          type="info"
          text
          dense
          dismissible
          color="#007AFF"
          class="mb-4"
          style="border-left: 4px solid #007AFF"
        >
          <div class="d-flex align-center">
            <v-icon small color="#007AFF" class="mr-2">mdi-robot-happy</v-icon>
            <span class="text-body-2">
              <strong>Sage HR handed off this search.</strong>
              You're now viewing the full candidate pool so you can make an
              informed, unbiased selection. All candidates meeting the criteria
              are shown — review, filter, and decide.
            </span>
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col :cols="hasResults ? 9 : 12">
        <v-card outlined class="pa-4 mb-4">
          <div class="d-flex align-center">
            <v-text-field
              v-model="searchQuery"
              label="Search for talent..."
              outlined
              dense
              hide-details
              color="#007AFF"
              class="mr-3"
              @keyup.enter="performSearch"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#007AFF">mdi-magnify</v-icon>
              </template>
            </v-text-field>
            <v-btn
              color="#007AFF"
              dark
              @click="performSearch"
              :loading="isLoading"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </div>

          <div class="mt-3">
            <span class="text-caption grey--text text--darken-1 mr-2"
              >Try:</span
            >
            <v-chip
              v-for="example in exampleSearches"
              :key="example"
              small
              outlined
              color="#007AFF"
              class="mr-2 mb-1"
              @click="
                searchQuery = example;
                performSearch();
              "
            >
              {{ example }}
            </v-chip>
          </div>

          <v-menu v-if="recentSearches.length > 0" offset-y bottom left>
            <template v-slot:activator="{ on, attrs }">
              <span
                class="text-caption grey--text text--darken-1 mr-2 mt-3 d-inline-flex align-center"
                style="cursor: pointer"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon x-small class="mr-1" color="#1565C0"
                  >mdi-history</v-icon
                >
                Recent
                <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
              </span>
            </template>
            <v-list dense>
              <v-list-item
                v-for="recent in recentSearches"
                :key="recent.query + recent.timestamp"
                @click="
                  searchQuery = recent.query;
                  performSearch();
                "
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small color="#1565C0">mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{
                    recent.query
                  }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="recent.resultCount" class="ml-2">
                  <span class="text-caption grey--text"
                    >({{ recent.resultCount }})</span
                  >
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="mt-3">
            <span class="text-caption grey--text text--darken-1 mr-2"
              >Talent Insights:</span
            >
            <v-chip
              v-for="insight in insightChips"
              :key="insight.label"
              small
              color="#FFF8E1"
              text-color="#F59E0B"
              class="mr-2 mb-1"
              @click="$router.push(insight.route)"
            >
              <v-icon x-small left color="#F59E0B">{{ insight.icon }}</v-icon>
              {{ insight.label }}
            </v-chip>
          </div>
        </v-card>

        <QueryClarification
          v-if="hasClarification"
          :clarification="clarification"
          @select="handleClarificationSelect"
          @skip="handleClarificationSkip"
          @dismiss="showClarification = false"
        />

        <v-alert v-if="hasError" type="error" dismissible class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <div v-if="isLoading" class="mb-4">
          <v-card outlined class="pa-5 text-center">
            <v-progress-circular
              indeterminate
              size="44"
              width="3"
              color="#007AFF"
              class="mb-3"
            />
            <div class="text-subtitle-2 font-weight-bold mb-1 theme-heading">
              Interpreting your query...
            </div>
            <div class="text-caption grey--text text--darken-1 mb-3">
              {{ searchStatusText }}
            </div>
            <v-progress-linear
              indeterminate
              color="#007AFF"
              rounded
              height="3"
              style="max-width: 260px; margin: 0 auto"
            />
          </v-card>
        </div>

        <div
          v-if="hasResults && (interpretation || queryUnderstanding)"
          class="mb-3"
        >
          <v-card outlined class="pa-3" style="border-left: 3px solid #007AFF">
            <div class="d-flex align-center">
              <v-icon small color="#007AFF" class="mr-2">mdi-brain</v-icon>
              <div class="text-caption grey--text font-weight-medium">
                Search Interpretation
              </div>
              <v-spacer />
              <v-btn
                x-small
                text
                color="#007AFF"
                @click="showInterpretationDetail = !showInterpretationDetail"
              >
                {{ showInterpretationDetail ? "Less" : "More" }}
                <v-icon x-small right>{{
                  showInterpretationDetail
                    ? "mdi-chevron-up"
                    : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </div>
            <div class="text-body-2 font-weight-medium mt-1">
              {{ interpretationSummary }}
            </div>

            <div v-if="interpretedFacets.length > 0" class="mt-2">
              <v-chip
                v-for="facet in interpretedFacets"
                :key="facet.name"
                small
                outlined
                :color="facetColor(facet)"
                class="mr-1 mb-1"
              >
                <v-icon x-small left :color="facetColor(facet)">{{
                  facetIcon(facet)
                }}</v-icon>
                {{ facet.name }}
                <span
                  v-if="facet.weight"
                  class="ml-1 text-caption"
                  style="opacity: 0.7"
                  >{{ Math.round(facet.weight * 100) }}%</span
                >
              </v-chip>
            </div>

            <v-expand-transition>
              <div
                v-if="showInterpretationDetail && scoringCriteria.length > 0"
                class="mt-3"
              >
                <v-divider class="mb-2" />
                <div class="text-caption grey--text text--darken-1 mb-1">
                  <v-icon x-small class="mr-1"
                    >mdi-clipboard-check-outline</v-icon
                  >
                  How each facet is evaluated:
                </div>
                <div
                  v-for="(criterion, idx) in scoringCriteria"
                  :key="idx"
                  class="d-flex align-start mb-1"
                >
                  <v-icon x-small color="#10B981" class="mr-1 mt-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  <div>
                    <span class="text-caption font-weight-medium"
                      >{{ criterion.facet }}:</span
                    >
                    <span class="text-caption grey--text text--darken-1 ml-1">{{
                      criterion.what_we_score || criterion.whatWeScore
                    }}</span>
                    <span
                      v-if="
                        criterion.detected_signal || criterion.detectedSignal
                      "
                      class="text-caption"
                      style="color: #007AFF"
                    >
                      (detected:
                      {{
                        criterion.detected_signal || criterion.detectedSignal
                      }})</span
                    >
                  </div>
                </div>
                <div v-if="interpretationConfidence" class="mt-2">
                  <v-chip x-small :color="confidenceColor" text-color="white">
                    Confidence:
                    {{ Math.round(interpretationConfidence * 100) }}%
                  </v-chip>
                </div>
              </div>
            </v-expand-transition>
          </v-card>
        </div>

        <div
          v-if="hasResults && categoryChips.length > 0"
          class="mb-3 d-flex flex-wrap align-center"
        >
          <v-chip
            v-for="cat in categoryChips"
            :key="cat.label"
            small
            :outlined="activeCategoryFilter !== cat.label"
            :color="cat.color"
            :text-color="
              activeCategoryFilter === cat.label ? 'white' : cat.color
            "
            class="mr-2 mb-1 category-chip"
            @click="toggleCategoryFilter(cat.label)"
          >
            {{ cat.count }} {{ cat.label }}
          </v-chip>
          <div v-if="activeCategoryFilter" class="text-caption ml-2 mb-1">
            <span class="theme-text-secondary"
              >{{ qualifiedCountDisplay }} qualified candidates (Match+)</span
            >
            <v-btn
              x-small
              text
              color="#EF4444"
              class="ml-1"
              @click="activeCategoryFilter = null"
            >
              <v-icon x-small>mdi-close</v-icon> Clear
            </v-btn>
          </div>
          <div v-else class="text-caption ml-2 mb-1 theme-text-secondary">
            {{ qualifiedCountDisplay }} qualified candidates (Match+)
          </div>
        </div>

        <div v-if="hasResults" class="mb-3">
          <div class="d-flex align-center mb-2">
            <div class="text-caption theme-text-secondary flex-grow-1">
              Showing {{ displayedResults.length }} of
              {{ totalResults }} results
              <span v-if="executionTimeMs"> in {{ executionTimeMs }}ms</span>
            </div>
            <v-btn
              x-small
              text
              :color="showScores ? '#007AFF' : '#9E9E9E'"
              @click="toggleScores"
            >
              <v-icon x-small left>{{
                showScores ? "mdi-eye" : "mdi-eye-off"
              }}</v-icon>
              {{ showScores ? "Scores On" : "Scores Off" }}
            </v-btn>
          </div>
          <SearchResultCard
            v-for="result in displayedResults"
            :key="result.profileId || result.name"
            :result="result"
            :show-scores="showScores"
            @view-profile="viewProfile"
          />
          <div v-if="hasMoreResults" class="text-center mt-4 mb-4">
            <v-btn
              outlined
              color="#007AFF"
              :loading="isLoadingMore"
              @click="loadMore"
            >
              <v-icon left>mdi-chevron-down</v-icon>
              Load More Results (Page {{ currentPage }} of {{ totalPages }})
            </v-btn>
          </div>
        </div>
      </v-col>

      <v-col cols="3" v-if="hasResults">
        <SearchFilters
          :available-seniorities="availableSeniorities"
          :available-clusters="availableClusters"
          :available-areas="availableAreas"
          :filters="filters"
          @update-filters="updateFilters"
          @clear-filters="clearFilters"
        />

        <FacetEditor
          v-if="facetOverrides"
          :facets="facetOverrides"
          class="mt-4"
          @update-facets="updateFacets"
          @rescore="rescore"
        />
      </v-col>
    </v-row>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import SearchResultCard from "../../components/Intelligence/SearchResultCard.vue";
import SearchFilters from "../../components/Intelligence/SearchFilters.vue";
import QueryClarification from "../../components/Intelligence/QueryClarification.vue";
import FacetEditor from "../../components/Intelligence/FacetEditor.vue";

export default {
  name: "IntelligenceDiscover",
  components: {
    SearchResultCard,
    SearchFilters,
    FacetEditor,
    QueryClarification,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      showClarification: false,
      showInterpretationDetail: false,
      fromSage: false,
      activeCategoryFilter: null,
      searchTextIndex: 0,
      searchTextInterval: null,
      exampleSearches: [
        "Python expert",
        "Senior data scientist",
        "Cloud architect",
        "Project manager with agile",
        "Full stack developer",
      ],
      insightChips: [
        {
          label: "Hidden Leaders",
          icon: "mdi-account-star",
          route: {
            name: "IntelligenceTalentInsights",
            query: { section: "hiddenLeaders" },
          },
        },
        {
          label: "Promotion Plateaus",
          icon: "mdi-chart-timeline-variant",
          route: {
            name: "IntelligenceTalentInsights",
            query: { section: "promotionPlateaus" },
          },
        },
        {
          label: "Succession Gaps",
          icon: "mdi-account-switch",
          route: {
            name: "IntelligenceTalentInsights",
            query: { section: "succession" },
          },
        },
        {
          label: "Emerging Skills",
          icon: "mdi-trending-up",
          route: {
            name: "IntelligenceTalentInsights",
            query: { section: "skillsEmergence" },
          },
        },
        {
          label: "Cross-Pollination",
          icon: "mdi-swap-horizontal",
          route: {
            name: "IntelligenceTalentInsights",
            query: { section: "crossPollination" },
          },
        },
        {
          label: "View Analytics →",
          icon: "mdi-chart-box-outline",
          route: { name: "IntelligenceAnalytics" },
        },
      ],
      defaultFacets: [
        { name: "Skills", weight: 1.0, importance: "primary" },
        { name: "Seniority", weight: 1.0, importance: "primary" },
        { name: "Cluster", weight: 1.0, importance: "nice-to-have" },
        { name: "Functional Area", weight: 1.0, importance: "nice-to-have" },
        { name: "Achievement Depth", weight: 1.0, importance: "nice-to-have" },
      ],
    };
  },
  computed: {
    ...mapState("intelligenceSearch", [
      "results",
      "interpretation",
      "queryUnderstanding",
      "clarification",
      "totalCandidates",
      "executionTimeMs",
      "showScores",
      "isLoading",
      "isLoadingMore",
      "hasError",
      "errorMessage",
      "filters",
      "facetOverrides",
      "recentSearches",
      "currentPage",
      "totalResults",
      "totalPages",
      "categoryCounts",
    ]),
    ...mapGetters("intelligenceSearch", [
      "filteredResults",
      "hasResults",
      "hasMoreResults",
      "hasClarification",
      "availableSeniorities",
      "availableClusters",
      "availableAreas",
    ]),
    interpretationSummary() {
      var interp = this.interpretation;
      if (interp && typeof interp === "object") {
        var s =
          interp.interpretationNote ||
          interp.interpretation_note ||
          interp.coreCapability ||
          interp.core_capability ||
          interp.human_summary ||
          interp.humanSummary ||
          interp.summary ||
          interp.naturalLanguage ||
          interp.full_explanation ||
          interp.fullExplanation ||
          "";
        if (s) return s;
      }
      if (interp && typeof interp === "string") return interp;
      var qu = this.queryUnderstanding;
      if (qu) {
        return (
          qu.interpretationNote ||
          qu.interpretation_note ||
          qu.coreCapability ||
          qu.core_capability ||
          ""
        );
      }
      return "";
    },
    interpretedFacets() {
      var interp = this.interpretation;
      if (interp && typeof interp === "object" && interp.facets && interp.facets.length > 0) {
        return interp.facets.map(function(f) {
          return {
            name: f.name || f.facet_name || f.facetName || "",
            weight: f.weight || null,
            priority: f.priority || f.importance || "general",
            description:
              f.description || f.extracted_value || f.extractedValue || "",
          };
        });
      }
      var qu = this.queryUnderstanding;
      if (qu && qu.facets && qu.facets.length > 0) {
        return qu.facets.map(function(f) {
          return {
            name: f.name || f.facet_name || f.facetName || "",
            weight: f.weight || null,
            priority: f.priority || f.importance || "general",
            description:
              f.description || f.extracted_value || f.extractedValue || "",
          };
        });
      }
      if (interp && typeof interp === "object") {
        var criteria = interp.scoring_criteria || interp.scoringCriteria || [];
        if (criteria.length > 0) {
          return criteria.map(function(c) {
            return {
              name: c.facet || c.facet_name || "",
              weight: null,
              priority: c.facet_type || c.facetType || "general",
              description:
                c.detected_signal || c.detectedSignal || c.what_we_score || "",
            };
          });
        }
      }
      return [];
    },
    scoringCriteria() {
      var interp = this.interpretation;
      if (interp && typeof interp === "object") {
        return interp.scoring_criteria || interp.scoringCriteria || [];
      }
      return [];
    },
    interpretationConfidence() {
      if (this.queryUnderstanding && this.queryUnderstanding.confidence)
        return this.queryUnderstanding.confidence;
      if (
        this.interpretation &&
        typeof this.interpretation === "object" &&
        this.interpretation.confidence
      )
        return this.interpretation.confidence;
      return null;
    },
    confidenceColor() {
      var c = this.interpretationConfidence;
      if (!c) return "#9E9E9E";
      if (c >= 0.8) return "#10B981";
      if (c >= 0.5) return "#F59E0B";
      return "#EF4444";
    },
    searchStatusText() {
      var texts = [
        "Interpreting your search...",
        "Evaluating candidate evidence...",
        "Synthesizing profile analysis...",
        "Preparing results...",
      ];
      return texts[this.searchTextIndex] || texts[0];
    },
    categoryChips() {
      var cc = this.categoryCounts;
      if (!cc) {
        var counts = {
          "Best Match": 0,
          "Strong Match": 0,
          "Potential Match": 0,
          "Potential Fit": 0,
          Match: 0,
          Consider: 0,
          Weak: 0,
        };
        var results = this.results || [];
        for (var i = 0; i < results.length; i++) {
          var cat =
            results[i].matchCategory || results[i].match_category || "Consider";
          if (counts[cat] !== undefined) counts[cat]++;
        }
        cc = {
          best_match: counts["Best Match"],
          match: counts["Match"] + counts["Strong Match"],
          potential_fit: counts["Potential Fit"] + counts["Potential Match"],
          consider: counts["Consider"] + counts["Weak"],
        };
      }
      var chips = [];
      if (cc.best_match > 0)
        chips.push({
          label: "Best Match",
          count: cc.best_match,
          color: "#10B981",
        });
      if (cc.match > 0)
        chips.push({ label: "Match", count: cc.match, color: "#007AFF" });
      if (cc.potential_fit > 0)
        chips.push({
          label: "Potential Fit",
          count: cc.potential_fit,
          color: "#F59E0B",
        });
      if (cc.consider > 0)
        chips.push({ label: "Consider", count: cc.consider, color: "#9E9E9E" });
      return chips;
    },
    qualifiedCountDisplay() {
      var cc = this.categoryCounts;
      if (cc) return (cc.best_match || 0) + (cc.match || 0);
      var results = this.results || [];
      var count = 0;
      for (var i = 0; i < results.length; i++) {
        var cat = results[i].matchCategory || results[i].match_category || "";
        if (cat === "Best Match" || cat === "Match" || cat === "Strong Match") count++;
      }
      return count;
    },
    displayedResults() {
      var results = this.filteredResults;
      if (!this.activeCategoryFilter) return results;
      var filter = this.activeCategoryFilter;
      var filterMap = {
        "Match": ["Match", "Strong Match"],
        "Potential Fit": ["Potential Fit", "Potential Match"],
        "Consider": ["Consider", "Weak"],
      };
      var acceptedCats = filterMap[filter] || [filter];
      return results.filter(function(r) {
        var cat = r.matchCategory || r.match_category || "Consider";
        return acceptedCats.indexOf(cat) >= 0;
      });
    },
  },
  watch: {
    hasClarification(val) {
      if (val) this.showClarification = true;
    },
    isLoading(val) {
      if (val) {
        this.searchTextIndex = 0;
        this.searchTextInterval = setInterval(() => {
          this.searchTextIndex = Math.min(this.searchTextIndex + 1, 3);
        }, 2500);
        this._searchStallTimeout = setTimeout(() => {
          if (this.isLoading) {
            console.warn("Search appears stalled after 20s");
          }
        }, 20000);
      } else {
        clearInterval(this.searchTextInterval);
        this.searchTextInterval = null;
        clearTimeout(this._searchStallTimeout);
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.searchTextInterval);
    clearTimeout(this._searchStallTimeout);
  },
  mounted() {
    this.loadRecentSearches();
    var q = this.$route.query.q;
    if (q) {
      this.searchQuery = q;
      this.fromSage = this.$route.query.from === "sage";
      this.performSearch();
    }
  },
  methods: {
    ...mapActions("intelligenceSearch", [
      "search",
      "keywordSearch",
      "rescore",
      "loadRecentSearches",
      "loadMore",
    ]),
    ...mapMutations("intelligenceSearch", [
      "setFilters",
      "clearFilters",
      "setFacetOverrides",
      "toggleScores",
    ]),
    async performSearch(options) {
      if (!this.searchQuery.trim()) return;
      if (!this.facetOverrides) {
        this.setFacetOverrides(this.defaultFacets.map((f) => ({ ...f })));
      }
      var skipClarification = (options && options.skipClarification) || false;
      await this.search({
        query: this.searchQuery.trim(),
        skipClarification: skipClarification,
      });
      this.loadRecentSearches();
    },
    updateFilters(filters) {
      this.setFilters({
        seniority: filters.seniority,
        functionalArea: filters.area,
        specialistCluster: filters.cluster,
        minScore: filters.minScore,
        skills: filters.skills,
      });
    },
    updateFacets(facets) {
      this.setFacetOverrides(facets);
    },
    handleClarificationSelect(suggestion) {
      var original = (this.searchQuery || "").trim();
      var suggestionLower = (suggestion || "").toLowerCase();
      var originalLower = original.toLowerCase();
      if (!original || suggestionLower.indexOf(originalLower) !== -1) {
        this.searchQuery = suggestion;
      } else if (originalLower.indexOf(suggestionLower) !== -1) {
        this.searchQuery = original;
      } else {
        this.searchQuery = original + " " + suggestion;
      }
      this.performSearch();
    },
    handleClarificationSkip(originalQuery) {
      this.searchQuery = originalQuery;
      this.performSearch({ skipClarification: true });
    },
    toggleCategoryFilter(label) {
      this.activeCategoryFilter =
        this.activeCategoryFilter === label ? null : label;
    },
    viewProfile(result) {
      this.$router.push({
        name: "IntelligenceProfile",
        params: { profileId: result.profileId },
        query: { from: "search" },
      });
    },
    facetColor(facet) {
      var p = facet.priority || "";
      if (p === "must-have" || p === "primary") return "#007AFF";
      if (p === "important" || p === "secondary") return "#10B981";
      return "#9E9E9E";
    },
    facetIcon(facet) {
      var p = facet.priority || "";
      if (p === "must-have" || p === "primary") return "mdi-star";
      if (p === "important" || p === "secondary") return "mdi-star-half-full";
      return "mdi-star-outline";
    },
  },
};
</script>

<style scoped>
.intelligence-discover {
  max-width: 1400px;
}
.theme-text-secondary {
  color: #666;
}
.v-application.theme--dark .theme-text-secondary {
  color: #aaa;
}
.category-chip {
  cursor: pointer;
  transition: all 0.15s ease;
}
.category-chip:hover {
  transform: translateY(-1px);
}
</style>
